import DeviceDataTable from '@components/_shared/DeviceDataTable/DeviceDataTable';
import UIBox from '@components/_shared/UIBox';
import PowinControllerCommandsModal from '@components/Battery/Powin/Commands/PowinController/PowinControllerCommandsModal';
import useGetInternalWidgetConfig from '@hooks/api/queries/widgets/useGetInternalWidgetConfig';
import { useCurrentPowinEmsDeviceContext } from '@hooks/useCurrentPowinEmsDevice';
import { generateMqttClient } from '@pages/_loader/initMqttClient.loader';
import { isActionRole } from '@src/services/auth';
import { UserRole } from '@src/services/auth/roles';
import { WidgetTypeDeviceList } from '@src/types/Widget';
import isCommandsEnabled from '@src/utils/isCommandsEnabled';
import storeConnector from '@store/storeConnector';
import { MqttClient } from 'mqtt';
import { useEffect, useMemo, useState } from 'react';

function PowinControllerList({
  role,
  isControlsInterLocked,
}: {
  role: UserRole;
  isControlsInterLocked: boolean;
}) {
  const { currentPowinEmsDevice, setCurrentPowinEmsDevice } =
    useCurrentPowinEmsDeviceContext();
  const { deviceId: currentPowinEmsId } = currentPowinEmsDevice || {
    deviceId: 'powinems_1',
  };

  const {
    data: widgetConfig,
    isLoading,
    isError,
  } = useGetInternalWidgetConfig<WidgetTypeDeviceList>('powinControllerList');

  const memoizedDevices = useMemo(
    () => widgetConfig?.devices || [],
    [widgetConfig?.devices],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: safe
  useEffect(() => {
    if (currentPowinEmsDevice === undefined) {
      setCurrentPowinEmsDevice(memoizedDevices[0]);
    }
  }, [memoizedDevices]);

  const memoizedDataPoints = useMemo(
    () => widgetConfig?.dataPoints || [],
    [widgetConfig?.dataPoints],
  );

  const memoizedPointConfigRefs = useMemo(
    () => widgetConfig?.pointConfigRefs || [],
    [widgetConfig?.pointConfigRefs],
  );

  const [cmdModal, setCmdModal] = useState(false);
  const [deviceIdForCmdModal, setDeviceIdForCmdModal] = useState<string>('');
  const deviceNameForCmdModal = useMemo(
    () =>
      memoizedDevices.find((device) => device.deviceId === deviceIdForCmdModal)
        ?.name,
    [memoizedDevices, deviceIdForCmdModal],
  );

  const handleRowSelection = (newDeviceId: string) => {
    if (newDeviceId) {
      setCurrentPowinEmsDevice(
        memoizedDevices.find((device) => device.deviceId === newDeviceId),
      );
    }
  };

  const closeCmdModal = () => {
    setCmdModal(false);
  };

  const [mqttClient, setMqttClient] = useState<MqttClient | undefined>(
    undefined,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: safe
  useEffect(() => {
    if (!cmdModal) {
      mqttClient?.end();
      setMqttClient(undefined);
      return () => {};
    }
    const startClient = async () => {
      const client = await generateMqttClient();
      setMqttClient(client);
    };

    startClient();
    return () => {
      mqttClient?.end();
    };
  }, [cmdModal]);

  return (
    <UIBox
      header={widgetConfig?.header}
      isLoading={isLoading}
      isError={isError || (isError && !widgetConfig)}
    >
      <DeviceDataTable
        widgetId='powin-controller-list'
        enableCmdBtnColumn={isCommandsEnabled()}
        disableCmdBtn={!isActionRole(role)}
        disableCmdBtnByInterlock={isControlsInterLocked}
        hideFooter
        devices={memoizedDevices}
        dataPoints={memoizedDataPoints}
        pointConfigRefs={memoizedPointConfigRefs}
        useDualListView={widgetConfig?.useDualListView ?? false}
        openCmdModal={(deviceId: string) => {
          setDeviceIdForCmdModal(deviceId);
          setCmdModal(true);
        }}
        rowSelectionModel={[currentPowinEmsId]}
        onRowSelectionModelChange={([newPowinEmsId]: string) =>
          handleRowSelection(newPowinEmsId)
        }
        getRowClassName={() => 'MuiDataGrid-clickable'}
      />
      {isCommandsEnabled() && isActionRole(role) && cmdModal && mqttClient ? (
        <PowinControllerCommandsModal
          mqttClient={mqttClient}
          deviceId={deviceIdForCmdModal}
          deviceName={deviceNameForCmdModal}
          onClose={() => closeCmdModal()}
        />
      ) : null}
    </UIBox>
  );
}

export default storeConnector(PowinControllerList, {
  config: ['siteMeta'],
  user: ['role'],
  service: ['isControlsInterLocked'],
});
